/**
 * Composer WhatsApp da mensagem de follow-up (toolbar, variáveis, mídia e IA).
 */
import React from 'react';
import { Upload, FileText, Image as ImageIcon, Mic } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import { useFollowUpMessageEditor, type MediaPickKind } from '../hooks/useFollowUpMessageEditor';
import FollowUpMediaPreview from './FollowUpMediaPreview';
import FollowUpMessageToolbar from './FollowUpMessageToolbar';

const MEDIA_MENU: {
    kind: MediaPickKind;
    label: string;
    accept: string;
    multiple: boolean;
    Icon: React.ComponentType<{ className?: string }>;
    iconWrapClass: string;
}[] = [
    { kind: 'document', label: 'Documento', accept: 'application/pdf,.doc,.docx,.xls,.xlsx', multiple: true, Icon: FileText, iconWrapClass: 'bg-info/15 text-info' },
    { kind: 'media', label: 'Fotos e vídeos', accept: 'image/*,video/*', multiple: true, Icon: ImageIcon, iconWrapClass: 'bg-primary-subtle text-primary' },
    { kind: 'audio', label: 'Áudio', accept: 'audio/*', multiple: true, Icon: Mic, iconWrapClass: 'bg-warning/15 text-warning' },
];

interface FollowUpMessageEditorProps {
    value: string;
    onChange: (text: string) => void;
    mediaUrl?: string | null;
    onMediaChange: (url: string | null) => void;
    fieldId?: string;
}

export default function FollowUpMessageEditor({
    value,
    onChange,
    mediaUrl,
    onMediaChange,
    fieldId = 'follow-up',
}: FollowUpMessageEditorProps) {
    const editor = useFollowUpMessageEditor({ value, onChange, mediaUrl, onMediaChange, fieldId });

    return (
        <section className="flex flex-col gap-s2" aria-labelledby={`${fieldId}-message`}>
            <div className="flex flex-wrap items-center justify-between gap-s2">
                <h4
                    id={`${fieldId}-message`}
                    className="text-xs font-semibold uppercase tracking-wide text-muted"
                >
                    Mensagem de Follow-up
                </h4>
                <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                        <Button type="button" variant="secondary" size="sm" disabled={editor.uploading} aria-haspopup="menu" aria-label="Adicionar mídia" className="min-h-11 touch-manipulation md:min-h-8">
                            <Upload className="size-4" aria-hidden="true" />
                            {editor.uploading ? 'Enviando...' : 'Adicionar Mídia'}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" side="bottom" sideOffset={6} className="z-50 w-56 max-w-[calc(100vw-2rem)]">
                        {MEDIA_MENU.map(({ kind, label, Icon, iconWrapClass, accept, multiple }) => (
                            <DropdownMenuItem key={kind} onSelect={() => editor.openFilePicker(accept, multiple)} className="gap-s3 py-s2">
                                <span className={`inline-flex size-8 shrink-0 items-center justify-center rounded-full ${iconWrapClass}`} aria-hidden="true">
                                    <Icon className="size-4" />
                                </span>
                                <span className="text-sm text-default">{label}</span>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
                <input
                    ref={editor.fileInputRef}
                    type="file"
                    id={editor.fileInputId}
                    name={editor.fileInputId}
                    className="hidden"
                    onChange={editor.handleMediaPick}
                />
            </div>

            <div className="overflow-hidden rounded-lg border border-border bg-surface">
                <FollowUpMessageToolbar
                    rewriting={editor.rewriting}
                    onFormat={editor.runFormat}
                    onRewrite={() => void editor.handleRewriteWithAi()}
                    onInsertVariable={(tag) =>
                        editor.applyEdit((text, start, end) => {
                            const next = text.slice(0, start) + tag + text.slice(end);
                            const cursor = start + tag.length;
                            return { next, selectionStart: cursor, selectionEnd: cursor };
                        })
                    }
                />
                <div className="flex flex-col">
                    {editor.mediaUrls.length > 0 && (
                        <ul className="flex gap-s2 overflow-x-auto border-b border-border px-s3 py-s3" aria-label="Mídias anexadas">
                            {editor.mediaUrls.map((url, index) => (
                                <li key={`${url}-${index}`}>
                                    <FollowUpMediaPreview url={url} onRemove={() => editor.removeMediaAt(index)} />
                                </li>
                            ))}
                        </ul>
                    )}
                    <textarea
                        ref={editor.textareaRef}
                        id={editor.messageInputId}
                        name={editor.messageInputName}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        aria-label="Mensagem de Follow-up"
                        rows={7}
                        className="min-h-36 w-full resize-y bg-transparent p-s4 font-normal text-sm text-default outline-none"
                    />
                </div>
            </div>
        </section>
    );
}
